/**
 * 系统剪贴板的图片读写（补充：剪切/拷贝/粘贴，跟 DESIGN.md §2.6 的文件
 * 网关同一类"平台能力网关"）。
 *
 * 走真正的系统剪贴板（Async Clipboard API），不是应用内部的暗道——
 * 写入的是普通 PNG 字节，能跟系统里其他应用互通，也能粘贴系统里其他地方
 * 拷贝来的图片。复用 io/codecs 现成的 PNG 编解码，不重新发明。
 *
 * 原生 `navigator.clipboard` 经由 {@link ClipboardLike} 注入，`ClipboardItem`
 * 的构造经由 `createItem` 注入——同 io/gateway 的 FSA 实现同一套测试纪律：
 * 测试用假实现驱动全部路径，不碰真实浏览器 API。
 */
import { decodeImage, decodedImageToRgba8, encodeImage } from './codecs';

export interface ClipboardImage {
  readonly width: number;
  readonly height: number;
  /** RGBA8，行优先。 */
  readonly pixels: Uint8Array;
}

/** 剪贴板里一条数据的最小面——原生 `ClipboardItem` 满足它。 */
export interface ClipboardReadItem {
  readonly types: readonly string[];
  getType(type: string): Promise<Blob>;
}

/** 原生 `Clipboard` 的最小面，够用且可假。 */
export interface ClipboardLike {
  write(items: readonly unknown[]): Promise<void>;
  read(): Promise<readonly ClipboardReadItem[]>;
}

export interface ClipboardGatewayDeps {
  /** 不可用（浏览器不支持/权限被拒/非安全上下文）时传 null。 */
  readonly clipboard: ClipboardLike | null;
  /** 构造一条要写入的数据。真实环境用全局 `ClipboardItem`，测试注入假实现。 */
  readonly createItem: (blobs: Readonly<Record<string, Blob>>) => unknown;
}

export interface ClipboardCallOptions {
  readonly signal?: AbortSignal;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/** 剪贴板里找不到图片，或读取权限被拒——区别于"剪贴板整体不可用"。 */
export class ClipboardImageNotFoundError extends Error {
  constructor() {
    super('剪贴板里没有图片内容');
    this.name = 'ClipboardImageNotFoundError';
  }
}

export class ClipboardGateway {
  readonly #deps: ClipboardGatewayDeps;

  constructor(deps: ClipboardGatewayDeps) {
    this.#deps = deps;
  }

  get available(): boolean {
    return this.#deps.clipboard !== null;
  }

  async writeImage(image: ClipboardImage, options: ClipboardCallOptions = {}): Promise<void> {
    const clipboard = this.#deps.clipboard;
    if (clipboard === null) throw new Error('剪贴板不可用（浏览器不支持，或页面没有相应权限）');
    throwIfAborted(options.signal);
    const bytes = await encodeImage('png', image.pixels, { width: image.width, height: image.height });
    throwIfAborted(options.signal);
    const blob = new Blob([bytes as BlobPart], { type: 'image/png' });
    await clipboard.write([this.#deps.createItem({ 'image/png': blob })]);
  }

  /**
   * 读剪贴板里的第一张图片。没有图片内容抛 {@link ClipboardImageNotFoundError}
   * （区别于剪贴板整体不可用那种更严重的失败），调用方据此给出"剪贴板里
   * 没有图片"而不是"剪贴板坏了"这样更准确的提示。
   */
  async readImage(options: ClipboardCallOptions = {}): Promise<ClipboardImage> {
    const clipboard = this.#deps.clipboard;
    if (clipboard === null) throw new Error('剪贴板不可用（浏览器不支持，或页面没有相应权限）');
    throwIfAborted(options.signal);
    const items = await clipboard.read();
    throwIfAborted(options.signal);
    for (const item of items) {
      const type = item.types.find((t) => t === 'image/png' || t === 'image/jpeg');
      if (type === undefined) continue;
      const blob = await item.getType(type);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      throwIfAborted(options.signal);
      const decoded = await decodeImage(bytes, options.signal === undefined ? {} : { signal: options.signal });
      return { width: decoded.width, height: decoded.height, pixels: decodedImageToRgba8(decoded) };
    }
    throw new ClipboardImageNotFoundError();
  }
}

/** 真实浏览器环境的依赖装配。不支持 Async Clipboard API 时 `clipboard` 为 null。 */
export function createBrowserClipboardDeps(): ClipboardGatewayDeps {
  const nav = globalThis.navigator as
    | (Navigator & { clipboard?: { write?: unknown; read?: unknown } })
    | undefined;
  const supported =
    nav?.clipboard !== undefined &&
    typeof nav.clipboard.write === 'function' &&
    typeof nav.clipboard.read === 'function';
  return {
    clipboard: supported ? (nav!.clipboard as unknown as ClipboardLike) : null,
    createItem: (blobs) => new ClipboardItem(blobs),
  };
}
